import { useTranslation } from 'react-i18next';
import { Route } from 'react-router-dom';

import { AlertMessage } from '~/common/components';
import { translateText } from '~/common/components/TranslatedLabel/TranslatedLabel';
import { AppRoutePaths } from '~/common/models/route.model';
import { lazyWithReload } from '~/common/utils/lazyWithReload';
import CancelledInvitePage from '~/routes/invites/invalidInvitePages/CancelledInvitePage';
import ExpiredInvitePage from '~/routes/invites/invalidInvitePages/ExpiredInvitePage';
import MobileAppRequiredAlert from '~/routes/MobileAppRequiredAlert';
import QuickpayLayout from '~/routes/quickpay/layout/QuickpayLayout';

const Quickpay = lazyWithReload(
  () => import('~/routes/quickpay/search/Quickpay')
);
const Payment = lazyWithReload(
  () => import('~/routes/quickpay/payment/Payment')
);
const Confirmation = lazyWithReload(
  () => import('~/routes/quickpay/confirmation/Confirmation')
);

// NOTE: This must be a hook-like function (not a module-level JSX constant)
// so that `useTranslation` can subscribe to `languageChanged` and force a
// re-render, making the `translateText(...)` calls below reactive to locale
// switches. Named with a `use` prefix so React's rules-of-hooks lint
// correctly validates the internal `useTranslation` call; it returns a
// `<Route>` fragment meant to be inlined directly as children of `<Routes>`.
const usePublicRoutes = () => {
  useTranslation(['common']);

  return (
    <>
      <Route
        path={AppRoutePaths.MOBILE_APP_REQUIRED}
        element={<MobileAppRequiredAlert />}
      />
      <Route
        path={AppRoutePaths.PAGE_NOT_FOUND}
        element={
          <AlertMessage
            header={translateText(
              'common:alertMsg.messageView.pageNotFound.header'
            )}
            linkBtn={translateText(
              'common:alertMsg.messageView.pageNotFound.button'
            )}
            topic="pageNotFound"
            id="page-not-found"
          />
        }
      />
      <Route
        path={AppRoutePaths.UNAUTHORIZED}
        element={
          <AlertMessage
            header={translateText(
              'common:alertMsg.messageView.unauthorized.header'
            )}
            body={translateText(
              'common:alertMsg.messageView.unauthorized.body'
            )}
            linkBtn={translateText(
              'common:alertMsg.messageView.unauthorized.button'
            )}
            topic="unauthorizedError"
            id="unauthorized-access"
          />
        }
      />

      <Route
        path={AppRoutePaths.SERVICE_UNAVAILABLE}
        element={
          <AlertMessage
            header={translateText(
              'common:alertMsg.messageView.serviceUnavailable.header'
            )}
            body={translateText(
              'common:alertMsg.messageView.serviceUnavailable.body'
            )}
            linkBtn={translateText(
              'common:alertMsg.messageView.serviceUnavailable.button'
            )}
            topic="serviceUnavailable"
            id="service-unavailable"
          />
        }
      />
      <Route
        path={AppRoutePaths.CANCELLED_INVITE}
        element={<CancelledInvitePage />}
      />
      <Route
        path={AppRoutePaths.EXPIRED_INVITE}
        element={<ExpiredInvitePage />}
      />
      <Route path={AppRoutePaths.QUICK_PAY} element={<QuickpayLayout />}>
        <Route index element={<Quickpay />} />
        <Route path="payment" element={<Payment />} />
        <Route path="confirmation" element={<Confirmation />} />
      </Route>
    </>
  );
};

export default usePublicRoutes;
